<template>
  <div class="users-tab">
    <header class="page-header">
      <div class="header-left">
        <h1 class="page-title">👥 Управление пользователями</h1>
        <p class="page-subtitle">Создание и настройка доступа пользователей системы</p>
      </div>
      <div class="header-actions">
        <button v-if="hasPermission('manage_users')" @click="openCreateModal" class="btn-create">
          <span class="btn-icon">➕</span>
          Создать пользователя
        </button>
        <button @click="fetchUsers" class="btn-refresh" :disabled="loading">
          <span class="refresh-icon">🔄</span>
          Обновить
        </button>
      </div>
    </header>

    <!-- LOADING STATE -->
    <div v-if="loading && users.length === 0" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Загрузка пользователей...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-title">Ошибка загрузки данных</p>
      <p class="error-message">{{ error }}</p>
      <button @click="fetchUsers" class="btn-primary">Повторить попытку</button>
    </div>

    <!-- USERS TABLE -->
    <div v-else-if="users.length > 0" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Полное имя</th>
            <th>Телефон</th>
            <th>Email</th>
            <th>Статус</th>
            <th>Root Admin</th>
            <th>Последний вход</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.id }}</td>
            <td>
              <span class="username">{{ user.username }}</span>
            </td>
            <td>{{ user.full_name || '—' }}</td>
            <td>{{ user.phone_number || '—' }}</td>
            <td>{{ user.email || '—' }}</td>
            <td>
              <span class="status-badge" :class="user.is_active ? 'active' : 'inactive'">
                {{ user.is_active ? 'Активен' : 'Заблокирован' }}
              </span>
            </td>
            <td>
              <span v-if="user.is_root_admin" class="root-badge">🔒 Root</span>
              <span v-else>—</span>
            </td>
            <td>{{ formatDate(user.last_login) }}</td>
            <td>
              <div class="action-buttons" v-if="hasPermission('manage_users')">
                <button @click="editUser(user)" class="btn-action" title="Редактировать">
                  ✏️
                </button>
                <button @click="changePassword(user)" class="btn-action" title="Сменить пароль">
                  🔑
                </button>
                <button
                  v-if="!user.is_root_admin"
                  @click="confirmDelete(user)"
                  class="btn-action danger"
                  title="Удалить"
                >
                  🗑️
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- EMPTY STATE -->
    <div v-else class="empty-state">
      <div class="empty-icon">👥</div>
      <h2 class="empty-title">Нет пользователей</h2>
      <p class="empty-text">Создайте первого пользователя системы</p>
      <button v-if="hasPermission('manage_users')" @click="openCreateModal" class="btn-primary">
        Создать пользователя
      </button>
    </div>

    <!-- CREATE/EDIT MODAL -->
    <transition name="modal">
      <div v-if="showCreateModal || editingUser" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">{{ editingUser ? 'Редактировать пользователя' : 'Новый пользователь' }}</h2>
            <button class="modal-close" @click="closeModal">×</button>
          </div>
          <div class="modal-content">
            <div class="modal-two-column">
              <!-- Левая колонка - Инпуты -->
              <div class="left-column">
                <div class="form-row">
                  <div class="form-group">
                    <label>Username *</label>
                    <input
                      v-model="formData.username"
                      type="text"
                      :disabled="editingUser !== null"
                      placeholder="Логин"
                      class="form-input"
                    />
                  </div>
                  <div class="form-group" v-if="!editingUser">
                    <label>Пароль *</label>
                    <input
                      v-model="formData.password"
                      type="password"
                      placeholder="Минимум 6 символов"
                      class="form-input"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label>Полное имя</label>
                  <input
                    v-model="formData.full_name"
                    type="text"
                    placeholder="Иван Иванов"
                    class="form-input"
                  />
                </div>

                <!-- Email и телефон только при редактировании -->
                <div class="form-row" v-if="editingUser">
                  <div class="form-group">
                    <label>Телефон</label>
                    <input
                      v-model="formData.phone"
                      type="tel"
                      placeholder="+7 (700) 123-45-67"
                      class="form-input"
                    />
                  </div>
                  <div class="form-group">
                    <label>Email</label>
                    <input
                      v-model="formData.email"
                      type="email"
                      placeholder="user@example.com"
                      class="form-input"
                    />
                  </div>
                </div>

                <div class="form-group" v-if="editingUser">
                  <label class="checkbox-label">
                    <input
                      v-model="formData.is_active"
                      type="checkbox"
                      class="form-checkbox"
                    />
                    <span>Активен</span>
                  </label>
                </div>
              </div>

              <!-- Правая колонка - Права доступа -->
              <div class="right-column">
                <div class="permissions-section">
                  <h3 class="permissions-title">Права доступа</h3>

                  <!-- Страницы -->
                  <div class="permission-category">
                    <h4 class="category-title">📄 Страницы</h4>
                    <div class="permission-grid">
                      <label v-for="perm in pagePermissions" :key="perm.permission_name" class="permission-label">
                        <input
                          type="checkbox"
                          :value="perm.permission_name"
                          v-model="selectedPermissions"
                          class="permission-checkbox"
                        />
                        <span class="permission-text">{{ perm.description }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- Действия -->
                  <div class="permission-category">
                    <h4 class="category-title">⚡ Действия</h4>
                    <div class="permission-grid">
                      <label v-for="perm in actionPermissions" :key="perm.permission_name" class="permission-label">
                        <input
                          type="checkbox"
                          :value="perm.permission_name"
                          v-model="selectedPermissions"
                          class="permission-checkbox"
                        />
                        <span class="permission-text">{{ perm.description }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="btn-secondary">Отмена</button>
            <button @click="saveUser" class="btn-primary" :disabled="saving">
              {{ saving ? 'Сохранение...' : 'Сохранить' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- PASSWORD CHANGE MODAL -->
    <transition name="modal">
      <div v-if="changingPasswordFor" class="modal-overlay" @click="closePasswordModal">
        <div class="modal-container small" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">Сменить пароль</h2>
            <button class="modal-close" @click="closePasswordModal">×</button>
          </div>
          <div class="modal-content">
            <p class="modal-info">Пользователь: <strong>{{ changingPasswordFor.username }}</strong></p>
            <div class="form-group">
              <label>Новый пароль *</label>
              <input
                v-model="newPassword"
                type="password"
                placeholder="Минимум 6 символов"
                class="form-input"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closePasswordModal" class="btn-secondary">Отмена</button>
            <button @click="savePassword" class="btn-primary" :disabled="saving">
              {{ saving ? 'Сохранение...' : 'Сменить пароль' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- DELETE CONFIRMATION -->
    <transition name="modal">
      <div v-if="deletingUser" class="modal-overlay" @click="deletingUser = null">
        <div class="modal-container small danger" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">⚠️ Удалить пользователя?</h2>
            <button class="modal-close" @click="deletingUser = null">×</button>
          </div>
          <div class="modal-content">
            <p class="modal-warning">
              Вы действительно хотите удалить пользователя <strong>{{ deletingUser.username }}</strong>?
            </p>
            <p class="modal-warning-sub">Это действие нельзя отменить!</p>
          </div>
          <div class="modal-footer">
            <button @click="deletingUser = null" class="btn-secondary">Отмена</button>
            <button @click="deleteUser" class="btn-danger" :disabled="deleting">
              {{ deleting ? 'Удаление...' : 'Удалить' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { usePermissions } from '@/composables/usePermissions'

const API_BASE = 'https://mpapi.premside.uz/api/admin'

export default {
  name: 'UsersTab',
  setup() {
    const { hasPermission } = usePermissions()
    const users = ref([])
    const loading = ref(true)
    const error = ref(null)
    const saving = ref(false)
    const deleting = ref(false)

    // Modals
    const showCreateModal = ref(false)
    const editingUser = ref(null)
    const changingPasswordFor = ref(null)
    const deletingUser = ref(null)

    // Form data
    const formData = ref({
      username: '',
      password: '',
      full_name: '',
      phone: '',
      email: '',
      is_active: true
    })
    const newPassword = ref('')

    // Permissions data
    const selectedPermissions = ref([])

    // Статические права из PERMISSIONS_EXPLAINED.md
    const pagePermissions = ref([
      { id: 1, permission_name: 'view_dashboard', description: 'Доступ к главной странице (дашборду)', category: 'page' },
      { id: 2, permission_name: 'view_hidden', description: 'Доступ к скрытым заказам', category: 'page' },
      { id: 3, permission_name: 'view_clients', description: 'Доступ к номерам клиентов', category: 'page' },
      { id: 4, permission_name: 'view_users', description: 'Доступ к управлению пользователями и правами', category: 'page' },
      { id: 5, permission_name: 'view_settings', description: 'Доступ к настройкам системы', category: 'page' }
    ])

    const actionPermissions = ref([
      { id: 6, permission_name: 'manage_hidden_orders', description: 'Скрывать и восстанавливать заказы', category: 'action' },
      { id: 7, permission_name: 'manage_client_phones', description: 'Изменять и добавлять номера клиентов', category: 'action' }
    ])

    /**
     * Загрузка прав пользователя
     */
    const loadUserPermissions = async (userId) => {
      try {
        const response = await axios.get(`${API_BASE}/users/${userId}/permissions`)
        console.log('Loaded user permissions:', response.data)

        // Проверяем разные возможные форматы ответа
        let permissions = []
        if (response.data.permissions) {
          permissions = response.data.permissions
        } else if (response.data.data) {
          permissions = response.data.data
        } else if (Array.isArray(response.data)) {
          permissions = response.data
        }

        console.log('Processed permissions:', permissions)
        return permissions
      } catch (err) {
        console.error('Error loading user permissions:', err)
        return []
      }
    }

    /**
     * Открытие формы создания пользователя
     */
    const openCreateModal = () => {
      selectedPermissions.value = []
      showCreateModal.value = true
    }

    /**
     * Загрузка пользователей
     */
    const fetchUsers = async () => {
      loading.value = true
      error.value = null

      try {
        const response = await axios.get(`${API_BASE}/users`)
        users.value = response.data.data || []
      } catch (err) {
        console.error('Error fetching users:', err)
        error.value = err.response?.data?.message || err.message || 'Не удалось загрузить пользователей'
      } finally {
        loading.value = false
      }
    }

    /**
     * Редактирование пользователя
     */
    const editUser = async (user) => {
      editingUser.value = user
      formData.value = {
        username: user.username,
        password: '',
        full_name: user.full_name || '',
        phone: user.phone_number || '',
        email: user.email || '',
        is_active: user.is_active === 1
      }

      // Загружаем права пользователя
      const userPerms = await loadUserPermissions(user.id)
      console.log('User permissions received:', userPerms)

      // Извлекаем только имена прав
      selectedPermissions.value = userPerms.map(p => p.permission_name)
      console.log('Selected permissions after mapping:', selectedPermissions.value)
    }

    /**
     * Сохранение пользователя (создание или обновление)
     */
    const saveUser = async () => {
      // Валидация
      if (!formData.value.username) {
        alert('Укажите username')
        return
      }

      if (!editingUser.value && !formData.value.password) {
        alert('Укажите пароль')
        return
      }

      saving.value = true

      try {
        if (editingUser.value) {
          // Обновление
          await axios.put(`${API_BASE}/users/${editingUser.value.id}`, {
            full_name: formData.value.full_name,
            phone: formData.value.phone,
            email: formData.value.email,
            is_active: formData.value.is_active
          })

          // Обновляем права пользователя
          await axios.post(`${API_BASE}/users/${editingUser.value.id}/permissions`, {
            permissions: selectedPermissions.value
          })
        } else {
          // Создание
          const response = await axios.post(`${API_BASE}/users`, {
            username: formData.value.username,
            password: formData.value.password,
            full_name: formData.value.full_name,
            permissions: selectedPermissions.value
          })

          // Если есть права, назначаем их новому пользователю
          if (selectedPermissions.value.length > 0 && response.data.data?.id) {
            await axios.post(`${API_BASE}/users/${response.data.data.id}/permissions`, {
              permissions: selectedPermissions.value
            })
          }
        }

        closeModal()
        await fetchUsers()
      } catch (err) {
        console.error('Error saving user:', err)
        alert(err.response?.data?.message || err.message || 'Не удалось сохранить пользователя')
      } finally {
        saving.value = false
      }
    }

    /**
     * Смена пароля
     */
    const changePassword = (user) => {
      changingPasswordFor.value = user
      newPassword.value = ''
    }

    const savePassword = async () => {
      if (!newPassword.value) {
        alert('Укажите новый пароль')
        return
      }

      if (newPassword.value.length < 6) {
        alert('Пароль должен быть минимум 6 символов')
        return
      }

      saving.value = true

      try {
        await axios.post(`${API_BASE}/users/${changingPasswordFor.value.id}/password`, {
          new_password: newPassword.value
        })

        closePasswordModal()
        alert('Пароль успешно изменен')
      } catch (err) {
        console.error('Error changing password:', err)
        alert(err.response?.data?.message || err.message || 'Не удалось сменить пароль')
      } finally {
        saving.value = false
      }
    }

    /**
     * Удаление пользователя
     */
    const confirmDelete = (user) => {
      deletingUser.value = user
    }

    const deleteUser = async () => {
      deleting.value = true

      try {
        await axios.delete(`${API_BASE}/users/${deletingUser.value.id}`)

        deletingUser.value = null
        await fetchUsers()
      } catch (err) {
        console.error('Error deleting user:', err)
        alert(err.response?.data?.message || err.message || 'Не удалось удалить пользователя')
      } finally {
        deleting.value = false
      }
    }

    /**
     * Закрытие модалок
     */
    const closeModal = () => {
      showCreateModal.value = false
      editingUser.value = null
      formData.value = {
        username: '',
        password: '',
        full_name: '',
        phone: '',
        email: '',
        is_active: true
      }
      selectedPermissions.value = []
    }

    const closePasswordModal = () => {
      changingPasswordFor.value = null
      newPassword.value = ''
    }

    /**
     * Форматирование даты
     */
    const formatDate = (dateString) => {
      if (!dateString) return 'Никогда'

      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))

      if (hours < 1) {
        return 'Только что'
      } else if (hours < 24) {
        return `${hours} ч. назад`
      } else if (days < 7) {
        return `${days} дн. назад`
      } else {
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      }
    }


    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      loading,
      error,
      saving,
      deleting,
      showCreateModal,
      editingUser,
      changingPasswordFor,
      deletingUser,
      formData,
      newPassword,
      selectedPermissions,
      pagePermissions,
      actionPermissions,
      openCreateModal,
      fetchUsers,
      editUser,
      saveUser,
      changePassword,
      savePassword,
      confirmDelete,
      deleteUser,
      closeModal,
      closePasswordModal,
      formatDate,
      hasPermission
    }
  }
}
</script>

<style scoped>
.users-tab {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* LOADING */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1.5rem;
  color: #6b7280;
  font-size: 1rem;
}

/* ERROR */
.error-container {
  text-align: center;
  padding: 4rem 2rem;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.error-message {
  color: #6b7280;
  margin-bottom: 2rem;
}

.btn-primary {
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* TABLE */
.table-container {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
}

.data-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.data-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.data-table tbody tr:hover {
  background: #f9fafb;
}

.data-table td {
  padding: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.username {
  font-weight: 600;
  color: #111827;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.active {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.root-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
}

.btn-action:hover {
  background: #e5e7eb;
  transform: scale(1.1);
}

.btn-action.danger:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

/* MODAL */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-container {
  background: white;
  border-radius: 1rem;
  /* max-width: 90%; */
  width: 70%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
}

.modal-container.small {
  max-width: 400px;
}

.modal-container.danger {
  border: 2px solid #ef4444;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.modal-close {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  transform: rotate(90deg);
}

.modal-content {
  padding: 1.25rem;
}

.modal-info {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  color: #374151;
  font-size: 0.875rem;
}

.modal-warning {
  font-size: 1rem;
  color: #374151;
  margin-bottom: 0.5rem;
}

.modal-warning-sub {
  color: #ef4444;
  font-weight: 600;
  font-size: 0.875rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
  font-size: 0.813rem;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.form-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.btn-secondary {
  padding: 0.625rem 1.25rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  color: #374151;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.btn-danger {
  padding: 0.625rem 1.25rem;
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.empty-text {
  color: #6b7280;
  margin-bottom: 2rem;
}

/* ANIMATIONS */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}

/* ROLES */
.roles-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.role-badges {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.role-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.no-roles {
  color: #9ca3af;
  font-size: 0.875rem;
}

/* ROLES MANAGEMENT MODAL */
.roles-management h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
}

.current-roles {
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.role-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.role-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.chip-remove {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.chip-remove:hover {
  background: rgba(255, 255, 255, 0.3);
}

.available-roles {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.role-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.role-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s;
}

.role-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.1);
}

.role-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.role-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
}

.role-description {
  font-size: 0.75rem;
  color: #6b7280;
}

.btn-assign {
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-assign:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.btn-assign:active {
  transform: translateY(0);
}

.no-roles-message,
.loading-message {
  text-align: center;
  color: #9ca3af;
  padding: 1rem;
  font-size: 0.875rem;
}

/* TWO COLUMN LAYOUT */
.modal-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 1%;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.right-column {
  display: flex;
  flex-direction: column;
}

/* PERMISSIONS SECTION */
.permissions-section {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  height: 100%;
}

.permissions-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
}

.permission-category {
  margin-bottom: 1.25rem;
}

.permission-category:last-child {
  margin-bottom: 0;
}

.category-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
}

.permission-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.permission-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.permission-label:hover {
  border-color: #6366f1;
  background: #f9fafb;
}

.permission-checkbox {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: #6366f1;
}

.permission-text {
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
}
</style>
